import type { GccAiComplexity, GccDispositionTag } from './types'

// Zod-free so the UI can import it without pulling in the schema layer.

/**
 * Quick win, agentify first (WI 2806, Ravi KO1): a task that is low complexity and
 * can be agentified within the horizon is transformed before any of its work moves.
 * Complementary to automate first (WI 2048), which tests the share AI releases, not
 * how soon or how easily.
 */
export const GCC_AI_COMPLEXITIES: readonly GccAiComplexity[] = ['Low', 'Medium', 'High']

/** Default quick-win horizon in months; configurable per run in the wizard. */
export const GCC_DEFAULT_QUICK_WIN_HORIZON_MONTHS = 6

/**
 * Longest horizon a new run accepts, in whole months (WI 2886). 0 turns quick wins
 * off. Designs saved before WI 2886 may store up to GCC_QUICK_WIN_MAX_MONTHS.
 */
export const GCC_QUICK_WIN_HORIZON_MAX_MONTHS = 6

/** Longest monthsToImplement accepted from the LLM, in whole months. */
export const GCC_QUICK_WIN_MAX_MONTHS = 36

/** Shown wherever the horizon is summarised when it is 0 (WI 2886). */
export const GCC_QUICK_WIN_OFF_SUMMARY = 'Quick wins are off (0-month horizon): no task is flagged'

/** Dispositions that can be a quick win: the AI does the work, so there is something to agentify. */
export const GCC_QUICK_WIN_DISPOSITIONS: readonly GccDispositionTag[] = ['Automate', 'Agentify']

export const GCC_QUICK_WIN_LABEL = 'Quick win – agentify first'

export function isGccAiComplexity(value: unknown): value is GccAiComplexity {
  return typeof value === 'string' && (GCC_AI_COMPLEXITIES as readonly string[]).includes(value)
}

/** True for whole months from 1 to GCC_QUICK_WIN_MAX_MONTHS. Used for monthsToImplement. */
export function isValidQuickWinMonths(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= GCC_QUICK_WIN_MAX_MONTHS
}

/** True for a horizon a new run accepts: whole months from 0 (off) to GCC_QUICK_WIN_HORIZON_MAX_MONTHS (WI 2886). */
export function isValidQuickWinHorizon(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= GCC_QUICK_WIN_HORIZON_MAX_MONTHS
}

/**
 * The slider position for a horizon (WI 2886): a valid horizon as it is, a whole
 * number above the slider's range (a design saved before WI 2886) as the maximum,
 * and anything else as the default.
 */
export function toQuickWinHorizonSliderMonths(value: unknown): number {
  if (isValidQuickWinHorizon(value)) return value
  if (typeof value === 'number' && Number.isInteger(value) && value > GCC_QUICK_WIN_HORIZON_MAX_MONTHS) return GCC_QUICK_WIN_HORIZON_MAX_MONTHS
  return GCC_DEFAULT_QUICK_WIN_HORIZON_MONTHS
}

/**
 * True when a task is a quick win: Automate or Agentify, low complexity, and
 * implementable within the horizon. The share AI releases plays no part. A
 * horizon of 0 turns quick wins off.
 */
export function isQuickWin(
  disposition: GccDispositionTag,
  aiComplexity: GccAiComplexity | undefined,
  monthsToImplement: number | undefined,
  horizonMonths: number,
): boolean {
  return horizonMonths > 0
    && GCC_QUICK_WIN_DISPOSITIONS.includes(disposition)
    && aiComplexity === 'Low'
    && monthsToImplement !== undefined
    && monthsToImplement <= horizonMonths
}

/** Why a task is a quick win, e.g. "Low complexity · 4 months ≤ 6-month horizon". */
export function quickWinBasis(monthsToImplement: number, horizonMonths: number): string {
  return `Low complexity · ${monthsToImplement} month${monthsToImplement === 1 ? '' : 's'} ≤ ${horizonMonths}-month horizon`
}
